// pages/ServiceCatalog.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  Plus,
  Edit,
  Trash2,
  Search,
  X,
  Save,
  Loader,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const CATEGORIES = [
  "hardware",
  "software",
  "license",
  "service",
  "subscription",
  "installation",
  "other",
];
const BILLING_CYCLES = ["one_time", "monthly", "quarterly", "annually"];

const emptyForm = {
  name: "",
  description: "",
  sku: "",
  category: "service",
  unitPrice: 0,
  currency: "GBP",
  billingCycle: "one_time",
  taxRate: 20,
  minQuantity: 1,
  defaultQuantity: 1,
  isActive: true,
  termPrices: null, // null = flat pricing; array = [{termMonths, unitPrice}]
};

const ServiceCatalog = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const res = await apiService.getQuoteServices({ limit: 500 });
      if (res.success) setServices(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (service) => {
    setEditing(service);
    setForm({
      ...emptyForm,
      ...service,
      unitPrice: parseFloat(service.unitPrice),
      taxRate: parseFloat(service.taxRate),
      // Normalise term tiers so every row has numeric values
      termPrices:
        Array.isArray(service.termPrices) && service.termPrices.length > 0
          ? service.termPrices.map((t) => ({
              termMonths: parseInt(t.termMonths),
              unitPrice: parseFloat(t.unitPrice),
            }))
          : null,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      alert("Name is required");
      return;
    }
    const hasTiers =
      Array.isArray(form.termPrices) && form.termPrices.length > 0;
    if (!hasTiers && !parseFloat(form.unitPrice)) {
      alert(
        "Either a unit price or at least one term pricing tier is required",
      );
      return;
    }
    if (hasTiers) {
      const invalid = form.termPrices.some(
        (t) => !t.termMonths || isNaN(parseFloat(t.unitPrice)),
      );
      if (invalid) {
        alert("All term tiers need a term length (months) and a price");
        return;
      }
    }
    try {
      setSaving(true);
      // When using term tiers, keep unitPrice in sync with the shortest tier
      // so flat-price consumers (list views, legacy code) always get something sensible.
      const payload = { ...form };
      if (hasTiers) {
        const sorted = [...form.termPrices].sort(
          (a, b) => parseInt(a.termMonths) - parseInt(b.termMonths),
        );
        payload.termPrices = sorted.map((t) => ({
          termMonths: parseInt(t.termMonths),
          unitPrice: parseFloat(t.unitPrice),
        }));
        payload.unitPrice = payload.termPrices[0].unitPrice;
      } else {
        payload.termPrices = null;
      }
      if (editing) {
        await apiService.updateQuoteService(editing.id, payload);
      } else {
        await apiService.createQuoteService(payload);
      }
      setShowForm(false);
      fetchServices();
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Term tier helpers ──────────────────────────────────────────────────────
  const addTermTier = () => {
    setForm((prev) => ({
      ...prev,
      termPrices: [
        ...(prev.termPrices || []),
        { termMonths: "", unitPrice: "" },
      ],
    }));
  };

  const updateTermTier = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      termPrices: prev.termPrices.map((t, i) =>
        i === idx ? { ...t, [field]: value } : t,
      ),
    }));
  };

  const removeTermTier = (idx) => {
    setForm((prev) => {
      const next = prev.termPrices.filter((_, i) => i !== idx);
      return { ...prev, termPrices: next.length > 0 ? next : null };
    });
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`Delete "${service.name}"?`)) return;
    try {
      await apiService.deleteQuoteService(service.id);
      fetchServices();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const filtered = services.filter((s) => {
    const matchSearch =
      !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.sku?.toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "all" || s.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const fmt = (amount, currency = "GBP") =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
      amount || 0,
    );

  return (
    <div
      className={`min-h-screen ${
        isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <button
              onClick={() => navigate("/quotes")}
              className={`flex items-center gap-2 mb-2 ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              <ArrowLeft size={16} /> Back to Quotes
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="text-blue-400" size={24} />
              Service Catalog
            </h1>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              Manage services, licenses and products available for quoting
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            <Plus size={16} /> Add Service
          </button>
        </div>

        {/* Filters */}
        <div
          className={`rounded-lg border p-4 mb-4 flex gap-3 flex-wrap ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div className="flex-1 min-w-0 relative">
            <Search
              size={16}
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              placeholder="Search services..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-9 pr-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={`px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
              isDarkMode
                ? "bg-gray-800 border-gray-700 text-white"
                : "bg-white border-gray-300 text-gray-900"
            }`}
          >
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="p-12 text-center">
            <Loader className="animate-spin mx-auto" size={32} />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((s) => (
              <div
                key={s.id}
                className={`rounded-lg border p-4 transition-colors ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 hover:border-gray-600"
                    : "bg-white border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-semibold truncate ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {s.name}
                    </h3>
                    <p
                      className={`text-xs capitalize ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {s.category} • {s.billingCycle.replace("_", " ")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(s)}
                      className={`p-1.5 rounded ${
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(s)}
                      className="p-1.5 rounded text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {s.description && (
                  <p
                    className={`text-sm line-clamp-2 mb-3 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {s.description}
                  </p>
                )}
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    {Array.isArray(s.termPrices) && s.termPrices.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {[...s.termPrices]
                          .sort((a, b) => a.termMonths - b.termMonths)
                          .map((tier) => (
                            <span
                              key={tier.termMonths}
                              className="text-xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20"
                            >
                              {tier.termMonths}mo:{" "}
                              {fmt(tier.unitPrice, s.currency)}
                            </span>
                          ))}
                      </div>
                    ) : (
                      <span className="text-lg font-bold text-green-400">
                        {fmt(s.unitPrice, s.currency)}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      s.isActive
                        ? "bg-green-500/10 text-green-400"
                        : "bg-gray-500/10 text-gray-400"
                    }`}
                  >
                    {s.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border w-full max-w-2xl max-h-[90vh] flex flex-col ${
              isDarkMode
                ? "bg-[#13161B] border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`flex items-center justify-between p-4 border-b ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <h2 className="text-lg font-semibold">
                {editing ? "Edit Service" : "New Service"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className={`p-2 rounded-lg ${
                  isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["name", "Name *", "text"],
                ["sku", "SKU", "text"],
                ["taxRate", "Tax Rate (%)", "number"],
                ["minQuantity", "Min Quantity", "number"],
                ["defaultQuantity", "Default Quantity", "number"],
              ].map(([field, label, type]) => (
                <div key={field}>
                  <label
                    className={`block text-sm mb-1 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {label}
                  </label>
                  <input
                    type={type}
                    value={form[field]}
                    onChange={(e) =>
                      setForm({ ...form, [field]: e.target.value })
                    }
                    className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              ))}

              {/* ── Pricing: flat vs term tiers ───────────────────────────── */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Pricing
                  </label>
                  <button
                    type="button"
                    onClick={addTermTier}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <Plus size={12} />
                    Add term tier (12 / 24 / 36 mo…)
                  </button>
                </div>

                {/* No tiers → single flat price */}
                {(!Array.isArray(form.termPrices) ||
                  form.termPrices.length === 0) && (
                  <div>
                    <label
                      className={`block text-xs mb-1 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Unit Price *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.unitPrice}
                      onChange={(e) =>
                        setForm({ ...form, unitPrice: e.target.value })
                      }
                      className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                )}

                {/* Term tiers list */}
                {Array.isArray(form.termPrices) &&
                  form.termPrices.length > 0 && (
                    <div className="space-y-2">
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Unit price will be set automatically to the shortest
                        term&apos;s price.
                      </p>
                      {form.termPrices.map((tier, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="flex-1">
                            <label
                              className={`block text-xs mb-1 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Term (months)
                            </label>
                            <input
                              type="number"
                              min="1"
                              placeholder="e.g. 12"
                              value={tier.termMonths}
                              onChange={(e) =>
                                updateTermTier(
                                  idx,
                                  "termMonths",
                                  e.target.value,
                                )
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-700 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </div>
                          <div className="flex-1">
                            <label
                              className={`block text-xs mb-1 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Unit Price
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={tier.unitPrice}
                              onChange={(e) =>
                                updateTermTier(idx, "unitPrice", e.target.value)
                              }
                              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-700 text-white"
                                  : "bg-white border-gray-300 text-gray-900"
                              }`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeTermTier(idx)}
                            className="mt-5 p-1.5 text-red-400 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addTermTier}
                        className={`w-full px-3 py-1.5 rounded-lg border border-dashed text-xs flex items-center justify-center gap-1 transition-colors ${
                          isDarkMode
                            ? "border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400"
                            : "border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-600"
                        }`}
                      >
                        <Plus size={12} /> Add another tier
                      </button>
                    </div>
                  )}
              </div>
              <div>
                <label
                  className={`block text-sm mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) =>
                    setForm({ ...form, category: e.target.value })
                  }
                  className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className={`block text-sm mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Billing Cycle
                </label>
                <select
                  value={form.billingCycle}
                  onChange={(e) =>
                    setForm({ ...form, billingCycle: e.target.value })
                  }
                  className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {BILLING_CYCLES.map((c) => (
                    <option key={c} value={c}>
                      {c.replace("_", " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label
                  className={`block text-sm mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Description
                </label>
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  className={`w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
              <label
                className={`flex items-center gap-2 sm:col-span-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />
                Active (available in quotes)
              </label>
            </div>
            <div
              className={`flex justify-end gap-2 p-4 border-t ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <button
                onClick={() => setShowForm(false)}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCatalog;
